import axios from "axios"
import { getToken, forceRefresh, refreshIfExpiring } from "@/auth/keycloak"

// Serialize query params the way the Tools API expects: URLSearchParams encoding
// with array values collapsed to a single comma-joined param (e.g. stationKeys=a,b)
// rather than axios's default stationKeys[]=a&stationKeys[]=b. null/undefined are
// skipped (matching axios's own default), so absent params don't leak as "undefined".
const serializeParams = (params) => {
  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value)
    }
  })

  return searchParams.toString()
}

const toolsClient = axios.create({
  baseURL: process.env.VUE_APP_TOOLS_SERVICE_URL + "/",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json; charset=utf-8",
  },
  paramsSerializer: serializeParams,
})

toolsClient.interceptors.request.use(
  async (config) => {
    // Refresh ahead of expiry instead of waiting for the server to reject a dead
    // token. Keycloak's onTokenExpired only fires once the token is *already*
    // expired, so without this every token lifetime boundary costs a guaranteed
    // 401 plus a refresh plus a retry, in every open tab.
    if (getToken()) {
      try {
        await refreshIfExpiring()
      } catch (e) {
        // Send the stale token anyway: the 401 handler below is the backstop, and
        // keycloak-js has already decided on its own whether the session is dead.
        console.error("[TOOLS] Proactive token refresh failed", e)
      }
    }

    const token = getToken()

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
      // Remembered so the 401 handler can tell "the token we sent was rejected"
      // apart from "the token was already replaced while this was in flight".
      config._token = token
    }

    return config
  },
  (error) => {
    console.log("[TOOLS] Request error", error.message, error.response)
    return Promise.reject(error)
  }
)

toolsClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config

    // On 401, force a token refresh and retry the request once. A failed refresh
    // is deliberately not handled here: keycloak-js ends the session itself when
    // the refresh is genuinely rejected, and a transient failure has to leave the
    // session alone rather than log the user out (see src/auth/keycloak.js).
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true

      // forceRefresh() is updateToken(-1), which skips the expiry check and always
      // hits the token endpoint. If another request already refreshed while this
      // one was in flight, retry with the newer token instead of forcing a second
      // one — and if the 401 was never about the token (an authorization failure
      // returned with the wrong status), this stops every request in the batch
      // from burning its own refresh round-trip before failing anyway.
      if (original._token && original._token !== getToken()) {
        return toolsClient(original)
      }

      try {
        await forceRefresh()
      } catch (refreshError) {
        console.error("[TOOLS] Token refresh failed", refreshError)
        return Promise.reject(error)
      }

      // No need to re-set the Authorization header — toolsClient() re-runs the
      // request interceptor, which reads the refreshed token.
      return toolsClient(original)
    }

    if (error.response) {
      console.error("Server responded with an error:", error.response)
    } else if (error.request) {
      console.error("No response received:", error.request)
    } else {
      console.error("Request setup error:", error.message)
    }

    return Promise.reject(error)
  }
)

export default toolsClient
